<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import PipelinesAPI from 'dashboard/api/masifi/pipelines';
import LeadsAPI from 'dashboard/api/masifi/leads';
import { STAGE_TYPE_META } from '../stageTypes';

// Cambiar el TIPO de una etapa (admin) — la baranda que Kommo no tiene:
// retipar con vista previa de impacto REAL. El conteo se pide dedicado al
// abrir (stats solo con pipeline_id+stage_id): los stats del tablero están
// contaminados por los filtros activos y subestimarían el impacto.
const props = defineProps({
  pipelineId: { type: [Number, String], required: true },
  stages: { type: Array, default: () => [] },
});
const emit = defineEmits(['updated']);

const { t } = useI18n();

const dialogRef = ref(null);
const stage = ref(null);
const targetType = ref('open');
const activeCount = ref(null);
const isSaving = ref(false);

const SELECTABLE_TYPES = ['open', 'won', 'lost'];

const open = async targetStage => {
  stage.value = targetStage;
  targetType.value = targetStage.stage_type;
  activeCount.value = null;
  isSaving.value = false;
  dialogRef.value.open();
  try {
    const { data } = await LeadsAPI.stats({
      pipeline_id: props.pipelineId,
      stage_id: targetStage.id,
    });
    const row = (data?.payload || []).find(s => s.stage_id === targetStage.id);
    activeCount.value = row?.count || 0;
  } catch (error) {
    activeCount.value = 0;
  }
};
defineExpose({ open });

// Singleton: won/lost deshabilitados si OTRA etapa ya tiene ese tipo.
const takenBy = type =>
  props.stages.find(s => s.stage_type === type && s.id !== stage.value?.id);

const typeOptions = computed(() =>
  SELECTABLE_TYPES.map(type => {
    const owner = takenBy(type);
    return {
      type,
      icon: STAGE_TYPE_META[type].icon,
      label: t(STAGE_TYPE_META[type].labelKey),
      disabled: !!owner && type !== 'open',
      hint:
        owner && type !== 'open'
          ? t('LEADS.STAGE_TYPE_DIALOG.TAKEN', { name: owner.name })
          : t(`LEADS.STAGE_TYPE_DIALOG.HINT_${type.toUpperCase()}`),
    };
  })
);

const typeChanged = computed(
  () => stage.value && targetType.value !== stage.value.stage_type
);
// Quitar el ÚNICO cierre de este tipo: el pipeline queda sin forma de cerrar
// leads así hasta crear/retipar otra etapa — avisar explícito.
const removesClosing = computed(
  () =>
    typeChanged.value &&
    ['won', 'lost'].includes(stage.value.stage_type) &&
    !takenBy(stage.value.stage_type)
);
const impactLabel = computed(() => {
  if (!typeChanged.value || activeCount.value === null) return '';
  if (!activeCount.value) return t('LEADS.STAGE_TYPE_DIALOG.IMPACT_EMPTY');
  return t('LEADS.STAGE_TYPE_DIALOG.IMPACT', {
    count: activeCount.value,
    state: t(STAGE_TYPE_META[targetType.value].labelKey),
  });
});

const confirm = async () => {
  if (!typeChanged.value) {
    dialogRef.value.close();
    return;
  }
  isSaving.value = true;
  try {
    await PipelinesAPI.updateStage(props.pipelineId, stage.value.id, {
      pipeline_stage: { stage_type: targetType.value },
    });
    dialogRef.value.close();
    emit('updated');
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('LEADS.STAGE.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('LEADS.STAGE_TYPE_DIALOG.TITLE', { name: stage?.name || '' })"
    :description="t('LEADS.STAGE_TYPE_DIALOG.DESCRIPTION')"
    :confirm-button-label="t('LEADS.STAGE_TYPE_DIALOG.CONFIRM')"
    :is-loading="isSaving"
    :disable-confirm-button="isSaving"
    @confirm="confirm"
  >
    <div class="flex flex-col gap-2">
      <label
        v-for="option in typeOptions"
        :key="option.type"
        class="flex items-start gap-3 px-3 py-2.5 border rounded-lg cursor-pointer"
        :class="[
          targetType === option.type
            ? 'border-n-brand bg-n-alpha-2'
            : 'border-n-weak',
          option.disabled ? 'opacity-50 cursor-not-allowed' : '',
        ]"
      >
        <input
          v-model="targetType"
          type="radio"
          :value="option.type"
          :disabled="option.disabled"
          class="mt-1"
        />
        <span class="flex flex-col gap-0.5 min-w-0">
          <span
            class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
          >
            <span class="size-3.5 text-n-slate-10" :class="option.icon" />
            {{ option.label }}
          </span>
          <span class="text-xs text-n-slate-10">{{ option.hint }}</span>
        </span>
      </label>

      <p
        v-if="impactLabel"
        class="px-3 py-2 text-xs rounded-lg bg-n-slate-2 text-n-slate-11"
      >
        {{ impactLabel }}
      </p>
      <p
        v-if="removesClosing"
        class="px-3 py-2 text-xs rounded-lg bg-n-amber-3 text-n-amber-12"
      >
        {{
          t('LEADS.STAGE_TYPE_DIALOG.REMOVES_CLOSING', {
            type: t(STAGE_TYPE_META[stage.stage_type].labelKey),
          })
        }}
      </p>
    </div>
  </Dialog>
</template>
